import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Breadcrumb, Avatar, Dropdown, Space, Button } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  CheckSquareOutlined,
  UploadOutlined,
  ClearOutlined,
  InboxOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../store/authStore';
import { apiClient } from '../../api/axios';
import { features } from '../../config/features';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const handleLogout = async () => {
    try {
      // Server revokes via httpOnly cookie (body token optional for API clients).
      await apiClient.post('/auth/logout', {});
    } catch {
      // Best-effort server-side revocation; clear local session regardless
    }
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role?.name === 'ADMINISTRATOR';
  const canImport = isAdmin || user?.role?.name === 'ADMIN' || user?.role?.name === 'ASISTENT';

  const menuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: '/patients',
      icon: <TeamOutlined />,
      label: 'Pacienți',
      onClick: () => navigate('/patients'),
    },
    // Importul individual vechi (chestionar → ciornă de wizard) e Faza 2; importul
    // în registru, inclusiv pentru un singur pacient, se face prin „Import în lot”.
    ...(features.clinicalWorkflow
      ? [
          {
            key: '/import',
            icon: <UploadOutlined />,
            label: 'Import Pacient',
            onClick: () => navigate('/import'),
          },
        ]
      : []),
    ...(canImport
      ? [
          {
            key: '/registry-import',
            icon: <InboxOutlined />,
            label: 'Import în lot',
            onClick: () => navigate('/registry-import'),
          },
          {
            key: '/registry-reports',
            icon: <BarChartOutlined />,
            label: 'Rapoarte registru',
            onClick: () => navigate('/registry-reports'),
          },
        ]
      : []),
    // Faza 2 — „Sarcinile mele" există doar cu feature flag-ul fluxului clinic activ.
    ...(features.clinicalWorkflow
      ? [
          {
            key: '/tasks',
            icon: <CheckSquareOutlined />,
            label: 'Sarcinile mele',
            onClick: () => navigate('/tasks'),
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: '/users',
            icon: <SettingOutlined />,
            label: 'Utilizatori',
            onClick: () => navigate('/users'),
          },
          {
            key: '/admin/test-data',
            icon: <ClearOutlined />,
            label: 'Curățare date de test',
            onClick: () => navigate('/admin/test-data'),
          },
        ]
      : []),
  ];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <IdcardOutlined />,
      label: 'Profil operator',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        void handleLogout();
      },
    },
  ];

  const breadcrumbNameMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/patients': 'Pacienți',
    '/patients/new': 'Pacient Nou',
    '/registry-import': 'Import în lot',
    '/registry-reports': 'Rapoarte registru',
    '/tasks': 'Sarcinile mele',
    '/users': 'Utilizatori',
    '/admin': 'Administrare',
    '/admin/test-data': 'Curățare date de test',
    '/profile': 'Profil operator',
  };

  const pathSnippets = location.pathname.split('/').filter((i) => i);
  const breadcrumbItems = pathSnippets.map((_, index) => {
    const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
    return {
      title: breadcrumbNameMap[url] || pathSnippets[index],
    };
  });

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="dark" breakpoint="lg" collapsedWidth={64} onBreakpoint={(broken) => setCollapsed(broken)}>
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: collapsed ? 16 : 20,
            fontWeight: 'bold',
          }}
        >
          {collapsed ? (
            <img src="/hepadskin-logo.svg" alt="HEPASKIN" style={{ width: 36, height: 36 }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/hepadskin-logo.svg" alt="HEPASKIN" style={{ width: 32, height: 32 }} />
              <span>HEPASKIN</span>
            </div>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: '0 24px', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16, width: 64, height: 64 }}
          />
          <Space size={16}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <span>
                  {user?.firstName} {user?.lastName}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: '16px' }}>
          <Breadcrumb style={{ marginBottom: 16 }} items={breadcrumbItems} />
          <div style={{ padding: 24, minHeight: 360, background: '#fff', borderRadius: 8 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
